import React, { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import IndicatorChart from "components/atoms/chart/parts/IndicatorChart"
import { IndicatorChartLegFilter } from "components/atoms/chart/parts/ChartLegFilter"
import { convertDate } from "components/atoms/chart/logic/chartSpan"
import { get, map } from "lodash"
import { BaseTable } from "components/molecules/table"
import { tableColumns } from "config/table_columns"
import { getCountryIcon } from "utils/util"
import * as Formatter from "utils/Formatter"
import ClassNames from "classnames"
import uuid from "uuid"
import { useHistogram, useLineChart } from "components/utils/useChart"
import ReactResizeDetector from "react-resize-detector"
import APIs from "redux/apis"
import dayjs from "dayjs"

interface props {
  canvasId: string
}

const IndicatorHistoryBar: React.FC<{
  data: object
  width: number
}> = props => {
  const className = useMemo(() => {
    return `histogram-${uuid()}`
  }, [])
  const displayData = map(
    props.data,
    (data: { date: number; volume: number }) => {
      const date = convertDate(get(data, "Date"))
      return {
        name: dayjs(new Date(date[0], date[1], date[2])).format("YYYY/M/D"),
        volume: get(data, "Value"),
      }
    }
  )
  const createHistogram = useHistogram({
    entryPoint: `.${className}`,
    dataset: displayData,
    title: "",
    option: {
      width: props.width,
      height: 200,
      padding: [30, 0, 50, 0],
      label_rotate: {
        rotate: -45,
        x: -10,
        y: 20,
      },
      y_ticks: 5,
      notTooltip: true,
    },
  })

  useEffect(() => {
    if (!props.data || !props.width) return
    createHistogram()
  }, [props.data, props.width])
  return <div className={ClassNames("histogram", className)} />
}
const IndicatorLineChart: React.FC<{
  data: object
  width: number
}> = props => {
  const className = useMemo(() => {
    return `histogram-${uuid()}`
  }, [])
  const displayData = map(
    props.data,
    (data: { date: number; volume: number }) => {
      const date = convertDate(get(data, "Date"))
      return {
        name: dayjs(new Date(date[0], date[1], date[2])).format("YYYY/M/D"),
        volume: get(data, "Value"),
      }
    }
  )
  const createLineChart = useLineChart({
    entryPoint: `.${className}`,
    dataset: displayData,
    title: "",
    option: {
      width: props.width,
      height: 200,
      padding: [30, 0, 50, 0],
      label_rotate: {
        rotate: -45,
        x: -10,
        y: 20,
      },
      y_ticks: 5,
      notTooltip: true,
    },
  })

  useEffect(() => {
    if (!props.data || !props.width) return
    createLineChart()
  }, [props.data, props.width])
  return <div className={ClassNames("histogram", className)} />
}

const IndicatorChartComponent: React.FC<props> = props => {
  const { canvasId } = props
  const [symbolData, setSymbolData] = useState({})

  const indicatorChartSelector = (state: any) => state.indicatorChart[canvasId]
  const indicatorChart = useSelector(indicatorChartSelector)
  const icon = useMemo(() => {
    return getCountryIcon(indicatorChart)
  }, [indicatorChart])
  const container = useRef<HTMLDivElement>(null)

  const indicatorChartTargetDateSelector = (state: any) =>
    state.indicatorChart[canvasId].TargetDate
  const indicatorChartIDSelector = (state: any) =>
    state.indicatorChart[canvasId].ID
  const TargetDate = useSelector(indicatorChartTargetDateSelector)
  const ID = useSelector(indicatorChartIDSelector)

  useEffect(() => {
    if (!ID && !TargetDate) return
    ;(async () => {
      const data = await APIs.Chart.IndicatorChart({ ID, TargetDate })
      setSymbolData(data)
    })()
  }, [ID, TargetDate])

  const chartData = get(symbolData, "Chart")

  return (
    <div>
      <div className="indicator-chart-header">
        <div className="header-row">
          <div className="header-items">
            <i className={ClassNames("icon-solid ", icon)} />
          </div>
          <div className="header-items">
            {get(indicatorChart, "DisplayName")}
          </div>
          <div className="time-col">
            {Formatter.date(get(indicatorChart, "AnnouncedAt"), "MM/DD HH:mm")}
          </div>
        </div>
        <div className="header-row">
          <BaseTable
            data={[indicatorChart]}
            list={tableColumns.economics.header}
          />
        </div>
        <div className="header-row">
          <IndicatorChartLegFilter canvasId={canvasId} />
        </div>
      </div>
      {/*<div className="indicator-chart-body">*/}
      {/*  <IndicatorChart canvasId={canvasId} />*/}
      {/*</div>*/}
      <ReactResizeDetector
        handleWidth={true}
        render={() => {
          const barWidth: number = !container.current
            ? 0
            : container.current.offsetWidth
          return (
            <div
              ref={container}
              // className={props.className}
              style={{ display: "flex" }}
            >
              {!get(indicatorChart, "type") ? (
                <IndicatorLineChart data={chartData} width={barWidth} />
              ) : (
                <IndicatorHistoryBar data={chartData} width={barWidth} />
              )}
            </div>
          )
        }}
      />
    </div>
  )
}

export default IndicatorChartComponent
